import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { createSnapshot } from 'services/snapshotsService';
import { SnapshotCsvMappingFlow } from './components/SnapshotCsvMappingFlow';
import { SnapshotCsvUpload } from './components/SnapshotCsvUpload';
import { SnapshotWizardSteps } from './components/SnapshotWizardSteps';
import {
  SnapshotBackLink,
  SnapshotCard,
  SnapshotPageHeader,
  SnapshotPageShell,
  SnapshotPrimaryButton,
} from './components/snapshotUi';
import { getSnapshotApiErrorMessage } from './snapshotApiError';

const PROMPT_CHIPS = [
  'Target accounts',
  'Open deals',
  'Customers',
  'A region',
];

export function SnapshotCreate() {
  const { token } = useAuth() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const textColor = useColorModeValue('navy.700', 'white');
  const prefill = (location.state || {}) as {
    partnerCompanyName?: string;
    partnerEmail?: string;
    partnerCompanyDomain?: string;
  };

  const [step, setStep] = useState<'details' | 'mapping'>('details');
  const [partnerCompanyName, setPartnerCompanyName] = useState(
    prefill.partnerCompanyName || '',
  );
  const [partnerEmail, setPartnerEmail] = useState(prefill.partnerEmail || '');
  const [partnerCompanyDomain, setPartnerCompanyDomain] = useState(
    prefill.partnerCompanyDomain || '',
  );
  const [uploadPrompt, setUploadPrompt] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const goToMapping = () => {
    if (!partnerCompanyName.trim() || !partnerEmail.trim()) {
      toast({ title: 'Company and email are required', status: 'warning' });
      return;
    }
    if (!file) {
      toast({ title: 'Upload your account list', status: 'warning' });
      return;
    }
    setStep('mapping');
  };

  const handleCreate = async (mappings: {
    columnMapping: Record<string, string>;
    stageMapping: Record<string, 'lead' | 'opportunity' | 'customer'>;
  }) => {
    if (!token || !file) return;
    setSubmitting(true);
    try {
      await createSnapshot(token, {
        partnerCompanyName: partnerCompanyName.trim(),
        partnerEmail: partnerEmail.trim(),
        partnerCompanyDomain: partnerCompanyDomain.trim() || undefined,
        uploadPrompt: uploadPrompt.trim() || undefined,
        file,
        columnMapping: mappings.columnMapping,
        stageMapping: mappings.stageMapping,
      });
      toast({
        title: 'Invite sent',
        description:
          'Your snapshot appears on the list. Open it once your partner has uploaded their list.',
        status: 'success',
      });
      navigate('/admin/snapshots');
    } catch (err: unknown) {
      toast({
        title: getSnapshotApiErrorMessage(err, 'Could not create snapshot'),
        status: 'error',
      });
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'mapping' && file && token) {
    return (
      <SnapshotPageShell>
        <SnapshotBackLink />
        <SnapshotPageHeader
          title="New snapshot"
          description="Confirm how we read your CSV before we store it and invite your partner."
        />
        <SnapshotCsvMappingFlow
          token={token}
          file={file}
          submitLabel="Send invite"
          isSubmitting={submitting}
          onBack={() => setStep('details')}
          onSubmit={handleCreate}
        />
      </SnapshotPageShell>
    );
  }

  return (
    <SnapshotPageShell>
      <SnapshotBackLink />
      <SnapshotPageHeader
        title="New snapshot"
        description="Add their company and email, then upload your account list. We will email them to upload theirs."
        action={
          <SnapshotPrimaryButton onClick={goToMapping} flexShrink={0}>
            Continue
          </SnapshotPrimaryButton>
        }
      />

      <SnapshotWizardSteps current="upload" />

      <Flex gap={6} align="stretch" direction={{ base: 'column', lg: 'row' }}>
        <SnapshotCard flex={1}>
          <Text fontSize="xl" fontWeight="bold" color={textColor} mb={1}>
            Who is this snapshot with?
          </Text>
          <Text fontSize="sm" color="gray.500" mb={6} lineHeight="1.55">
            They receive an email to create a free account and upload their list.
          </Text>

          <FormControl mb={5} isRequired>
            <FormLabel fontSize="sm" fontWeight="600" color={textColor}>
              Company
            </FormLabel>
            <Input
              value={partnerCompanyName}
              onChange={(e) => setPartnerCompanyName(e.target.value)}
              placeholder="PiperChat"
            />
          </FormControl>
          <FormControl mb={5} isRequired>
            <FormLabel fontSize="sm" fontWeight="600" color={textColor}>
              Their email
            </FormLabel>
            <Input
              type="email"
              value={partnerEmail}
              onChange={(e) => setPartnerEmail(e.target.value)}
              placeholder="jess@piperchat.com"
            />
          </FormControl>
          <FormControl mb={2}>
            <FormLabel fontSize="sm" fontWeight="600" color={textColor}>
              What should they upload?{' '}
              <Text as="span" color="gray.400" fontWeight="normal">
                (optional)
              </Text>
            </FormLabel>
            <Textarea
              value={uploadPrompt}
              onChange={(e) => setUploadPrompt(e.target.value)}
              placeholder="e.g. Your target accounts for Q4"
              rows={3}
            />
          </FormControl>
          <Wrap mt={3} spacing={2}>
            {PROMPT_CHIPS.map((chip) => (
              <WrapItem key={chip}>
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="16px"
                  onClick={() => setUploadPrompt(chip)}
                >
                  {chip}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        </SnapshotCard>

        <SnapshotCard flex={1.15}>
          <SnapshotCsvUpload file={file} onFileChange={setFile} />
        </SnapshotCard>
      </Flex>
    </SnapshotPageShell>
  );
}
